/**
 * HelpCenterV2 - Centre d'aide complet avec FAQ, Vidéos et Chat Support
 * Design moderne glassmorphism
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HelpCircle, X, Search, MessageCircle, Video, BookOpen, ChevronDown, Play, ExternalLink, Send, User, Bot, Sparkles, Clock, ThumbsUp, ThumbsDown, Phone, Mail, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';

// FAQ Data par espace
import { FAQ_DATA, VIDEOS_DATA } from './helpCenterData';

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => (
  <div className="border border-gray-200 rounded-xl overflow-hidden mb-2 hover:border-amber-300 transition-colors">
    <button
      onClick={onClick}
      className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
    >
      <span className="font-medium text-gray-900 pr-4">{question}</span>
      <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
    </button>
    {isOpen && (
      <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-3 bg-gray-50">
        {answer}
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-200">
          <span className="text-xs text-gray-400">Cette réponse vous a-t-elle aidé ?</span>
          <button className="text-gray-400 hover:text-green-500 transition-colors">
            <ThumbsUp className="w-4 h-4" />
          </button>
          <button className="text-gray-400 hover:text-red-500 transition-colors">
            <ThumbsDown className="w-4 h-4" />
          </button>
        </div>
      </div>
    )}
  </div>
);

// Video Card
const VideoCard = ({ video, onClick }) => (
  <button
    onClick={onClick}
    className="w-full flex items-center gap-4 p-4 bg-white border border-gray-200 rounded-xl hover:border-amber-300 hover:shadow-md transition-all group"
  >
    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-2xl flex-shrink-0 group-hover:scale-105 transition-transform">
      {video.thumbnail}
    </div>
    <div className="flex-1 text-left">
      <h4 className="font-semibold text-gray-900 group-hover:text-amber-600 transition-colors">{video.title}</h4>
      <div className="flex items-center gap-3 mt-1">
        <Badge variant="outline" className="text-xs">{video.category}</Badge>
        <span className="flex items-center gap-1 text-xs text-gray-500">
          <Clock className="w-3 h-3" />
          {video.duration}
        </span>
      </div>
    </div>
    <Play className="w-8 h-8 text-amber-500 group-hover:text-amber-600 transition-colors" />
  </button>
);

// Chat Message
const ChatMessage = ({ message, isUser }) => (
  <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
      isUser ? 'bg-amber-500' : 'bg-gradient-to-br from-violet-500 to-purple-600'
    }`}>
      {isUser ? <User className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
    </div>
    <div className={`max-w-[80%] p-3 rounded-2xl ${
      isUser 
        ? 'bg-amber-500 text-white rounded-br-md' 
        : 'bg-gray-100 text-gray-800 rounded-bl-md'
    }`}>
      <p className="text-sm">{message.text}</p>
      <span className={`text-xs mt-1 block ${isUser ? 'text-amber-200' : 'text-gray-400'}`}>
        {message.time}
      </span>
    </div>
  </div>
);

// Main HelpCenterV2 Component
export default function HelpCenterV2({ space = 'member' }) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('faq');
  const [searchQuery, setSearchQuery] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [chatMessages, setChatMessages] = useState([
    { id: 1, text: 'Bonjour ! Je suis votre assistant COPPAM. Comment puis-je vous aider ?', isUser: false, time: 'Maintenant' }
  ]);
  const [chatInput, setChatInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const chatEndRef = useRef(null);

  const faqData = FAQ_DATA[space] || FAQ_DATA.member;
  const videosData = VIDEOS_DATA[space] || VIDEOS_DATA.member;

  // Filter FAQ based on search
  const filteredFAQ = searchQuery
    ? faqData.map(category => ({
        ...category,
        questions: category.questions.filter(
          q => q.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
               q.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.questions.length > 0)
    : faqData;

  // Auto scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Handle chat send
  const handleSendChat = () => {
    if (!chatInput.trim()) return;

    const userMessage = {
      id: chatMessages.length + 1,
      text: chatInput,
      isUser: true,
      time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    };

    setChatMessages(prev => [...prev, userMessage]);
    setChatInput('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const botResponses = [
        'Je comprends votre question. Laissez-moi vérifier cela pour vous...',
        'Voici ce que je peux vous proposer : consultez la FAQ pour plus de détails ou contactez notre support.',
        'Pour cette demande spécifique, je vous recommande de contacter directement notre équipe support par email.',
        'Avez-vous consulté notre section tutoriels vidéo ? Elle contient des guides détaillés.',
        'Je suis là pour vous aider ! N\'hésitez pas à reformuler votre question si nécessaire.'
      ];
      
      const botMessage = {
        id: chatMessages.length + 2,
        text: botResponses[Math.floor(Math.random() * botResponses.length)],
        isUser: false,
        time: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      };

      setIsTyping(false);
      setChatMessages(prev => [...prev, botMessage]);
    }, 1500);
  };

  const handleVideoClick = (video) => {
    alert(`Lecture de la vidéo: ${video.title}\n\nFonctionnalité en cours de développement.`);
  };

  const resetTour = () => {
    if (typeof window.resetWelcomeTour === 'function') {
      window.resetWelcomeTour();
      setIsOpen(false);
      return;
    }
    // Fallback V35.44 : si l'API globale n'est pas exposée (composant non monté ou autre tour),
    // on nettoie tous les flags tour connus et on recharge la page pour que le tour démarre proprement.
    try {
      ['member', 'cooper', 'expert', 'admin'].forEach((space) => {
        localStorage.removeItem(`welcome_tour_${space}_completed`);
        sessionStorage.removeItem(`welcome_tour_${space}_skipped_session`);
      });
    } catch { /* ignore */ }
    setIsOpen(false);
    // Reload pour réinitialiser le composant WelcomeTour
    setTimeout(() => window.location.reload(), 100);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 shadow-lg shadow-amber-500/30 flex items-center justify-center hover:scale-110 hover:shadow-xl transition-all duration-300 group"
        data-testid="help-center-btn"
      >
        <HelpCircle className="w-6 h-6 text-white group-hover:scale-110 transition-transform" />
        <span className="absolute inset-0 rounded-full bg-white/20 animate-ping" style={{ animationDuration: '2s' }} />
      </button>

      {/* Help Panel */}
      {isOpen && createPortal(
        <div className="fixed inset-0 z-[90] flex justify-end">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="relative w-full max-w-xl bg-white shadow-2xl animate-in slide-in-from-right duration-300 flex flex-col">
            {/* Header */}
            <div className="p-6 bg-gradient-to-r from-amber-500 to-orange-600 text-white">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Centre d&apos;Aide</h2>
                    <p className="text-sm text-amber-100">FAQ • Vidéos • Support</p>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-200" />
                <Input
                  placeholder="Rechercher dans l'aide..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-white/20 border-white/30 text-white placeholder:text-amber-200 focus:bg-white/30"
                />
              </div>
            </div>
            
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <TabsList className="mx-4 mt-4 bg-gray-100">
                <TabsTrigger value="faq" className="flex-1 data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  <BookOpen className="w-4 h-4 mr-2" />
                  FAQ
                </TabsTrigger>
                <TabsTrigger value="videos" className="flex-1 data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  <Video className="w-4 h-4 mr-2" />
                  Vidéos
                </TabsTrigger>
                <TabsTrigger value="chat" className="flex-1 data-[state=active]:bg-amber-500 data-[state=active]:text-white">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat
                </TabsTrigger>
              </TabsList>
              
              {/* FAQ Tab */}
              <TabsContent value="faq" className="flex-1 overflow-hidden p-4">
                <ScrollArea className="h-full pr-4">
                  {filteredFAQ.length === 0 ? (
                    <div className="text-center py-8">
                      <Search className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500">Aucun résultat trouvé</p>
                    </div>
                  ) : (
                    filteredFAQ.map((category, catIndex) => (
                      <div key={catIndex} className="mb-6">
                        <h3 className="flex items-center gap-2 font-bold text-gray-900 mb-3">
                          <span className="text-xl">{category.icon}</span>
                          {category.category}
                        </h3>
                        {category.questions.map((faq, faqIndex) => {
                          const key = `${catIndex}-${faqIndex}`;
                          return (
                            <FAQItem
                              key={key}
                              question={faq.q}
                              answer={faq.a}
                              isOpen={openFAQ === key}
                              onClick={() => setOpenFAQ(openFAQ === key ? null : key)}
                            />
                          );
                        })}
                      </div>
                    ))
                  )}
                  
                  {/* Replay Tour Button */}
                  <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                    <h4 className="font-semibold text-amber-800 mb-2">Relancer le tour guidé</h4>
                    <p className="text-sm text-amber-600 mb-3">Besoin d&apos;une mise à jour ? Refaites le tour de votre espace.</p>
                    <Button onClick={resetTour} variant="outline" className="w-full border-amber-300 text-amber-700 hover:bg-amber-100">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Relancer le tour
                    </Button>
                  </div>
                </ScrollArea>
              </TabsContent>
              
              {/* Videos Tab */}
              <TabsContent value="videos" className="flex-1 overflow-hidden p-4">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-3">
                    {videosData.map(video => (
                      <VideoCard key={video.id} video={video} onClick={() => handleVideoClick(video)} />
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-gray-50 rounded-xl text-center">
                    <p className="text-sm text-gray-500 mb-2">Besoin d&apos;autres tutoriels ?</p>
                    <Button variant="outline" className="text-amber-600 border-amber-300">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Voir toutes les vidéos
                    </Button>
                  </div>
                </ScrollArea>
              </TabsContent>
              
              {/* Chat Tab */}
              <TabsContent value="chat" className="flex-1 overflow-hidden flex flex-col">
                {/* Chat Messages */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4">
                    {chatMessages.map(message => (
                      <ChatMessage key={message.id} message={message} isUser={message.isUser} />
                    ))}
                    {isTyping && (
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
                          <Bot className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-gray-100 p-3 rounded-2xl rounded-bl-md">
                          <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Chat Input */}
                <div className="p-4 border-t bg-gray-50">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Tapez votre message..."
                      value={chatInput}
                      onChange={(e) => setChatInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSendChat()}
                      className="flex-1"
                    />
                    <Button onClick={handleSendChat} className="bg-amber-500 hover:bg-amber-600">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {/* Quick Actions */}
                  <div className="flex gap-2 mt-3">
                    <Button variant="outline" size="sm" className="text-xs">
                      <Phone className="w-3 h-3 mr-1" />
                      Appeler
                    </Button>
                    <Button variant="outline" size="sm" className="text-xs">
                      <Mail className="w-3 h-3 mr-1" />
                      Email
                    </Button>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
